import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Clock, MapPin, Camera, MessageSquare } from 'lucide-react';
import { TaskMediaUpload } from '@/components/flexhelper/tasks/task-media-upload';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';

export function ActiveTaskCard() {
  const [showMediaUpload, setShowMediaUpload] = useState(false);
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  const [note, setNote] = useState('');
  const { toast } = useToast();

  const activeTask = {
    id: '1',
    title: 'HVAC Maintenance - Building A',
    description: 'Regular maintenance check of the HVAC system',
    location: 'Building A, Floor 2',
    startTime: '09:00 AM',
    estimatedDuration: '2 hours',
    progress: 65,
    status: 'in_progress',
  };

  const handleMediaUpload = (files: File[], caption: string) => {
    // Here you would handle the file upload to your backend
    console.log('Uploading files:', files, 'with caption:', caption);
    toast({
      title: 'Media Added',
      description: `${files.length} files uploaded successfully`,
    });
    setShowMediaUpload(false);
  };

  const handleAddNote = () => {
    if (!note.trim()) return;
    // Here you would save the note to your backend
    console.log('Adding note:', note);
    toast({
      title: 'Note Added',
      description: 'Task note has been saved',
    });
    setNote('');
    setShowNoteDialog(false);
  };

  const handleCompleteTask = () => {
    // Here you would update the task status in your backend
    console.log('Completing task:', activeTask.id);
    toast({
      title: 'Task Completed',
      description: 'Task has been marked as complete',
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Active Task</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          <div>
            <h3 className="font-semibold text-lg">{activeTask.title}</h3>
            <p className="text-sm text-muted-foreground mt-1">
              {activeTask.description}
            </p>
          </div>

          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-1">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <span>Started: {activeTask.startTime}</span>
            </div>
            <div className="flex items-center gap-1">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              <span>{activeTask.location}</span>
            </div>
            <Badge variant="secondary">
              {activeTask.estimatedDuration} remaining
            </Badge>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Progress</span>
              <span className="font-medium">{activeTask.progress}%</span>
            </div>
            <Progress value={activeTask.progress} className="h-2" />
          </div>

          <div className="flex gap-2">
            <Button 
              className="flex-1" 
              variant="outline"
              onClick={() => setShowMediaUpload(true)}
            >
              <Camera className="mr-2 h-4 w-4" />
              Add Photo
            </Button>
            <Button 
              className="flex-1" 
              variant="outline"
              onClick={() => setShowNoteDialog(true)}
            >
              <MessageSquare className="mr-2 h-4 w-4" />
              Add Note
            </Button>
            <Button 
              className="flex-1"
              onClick={handleCompleteTask}
            >
              Complete Task
            </Button>
          </div>
        </div>

        {/* Media Upload Dialog */}
        <TaskMediaUpload
          open={showMediaUpload}
          onClose={() => setShowMediaUpload(false)}
          onUpload={handleMediaUpload}
        />

        {/* Note Dialog */}
        <Dialog open={showNoteDialog} onOpenChange={setShowNoteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Task Note</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Enter your note here..."
                rows={4}
              />
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowNoteDialog(false)}
                >
                  Cancel
                </Button>
                <Button onClick={handleAddNote}>
                  Save Note
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}